/**
 * Platform Writer Registry
 *
 * Central registry for all platform-specific CSV writers.
 * Maps platform + version to the appropriate writer implementation.
 *
 * When platform formats change:
 * 1. Create a new version file (e.g., meta/v2.ts)
 * 2. Update the registry to point 'latest' to the new version
 * 3. Old versions remain available for backward compatibility
 */

import { Platform, PlatformWriter, PlatformInfo } from '../types';

// Import all platform writers
import { MetaWriterV1 } from './meta/v1';
import { GoogleAdsWriterV1 } from './google-ads/v1';
import { TwitterWriterV1 } from './twitter/v1';
import { PinterestWriterV1 } from './pinterest/v1';

/**
 * Registry of platform writers
 * Each platform can have multiple versions
 */
const WRITERS: Record<Platform, Record<string, () => PlatformWriter>> = {
  meta: {
    v1: () => new MetaWriterV1(),
    latest: () => new MetaWriterV1(),
  },
  google_ads: {
    v1: () => new GoogleAdsWriterV1(),
    latest: () => new GoogleAdsWriterV1(),
  },
  twitter: {
    v1: () => new TwitterWriterV1(),
    latest: () => new TwitterWriterV1(),
  },
  pinterest: {
    v1: () => new PinterestWriterV1(),
    latest: () => new PinterestWriterV1(),
  },
};

/**
 * Platform information for UI display
 */
const PLATFORM_INFO: Record<Platform, PlatformInfo> = {
  meta: {
    id: 'meta',
    name: 'Meta (Facebook/Instagram)',
    icon: '📘',
    versions: ['v1'],
    latestVersion: 'v1',
    description: 'Export for Meta Ads Manager bulk upload',
  },
  google_ads: {
    id: 'google_ads',
    name: 'Google Ads',
    icon: '🔍',
    versions: ['v1'],
    latestVersion: 'v1',
    description: 'Export for Google Ads Editor import',
  },
  twitter: {
    id: 'twitter',
    name: 'Twitter/X',
    icon: '🐦',
    versions: ['v1'],
    latestVersion: 'v1',
    description: 'Export for Twitter Ads Editor',
  },
  pinterest: {
    id: 'pinterest',
    name: 'Pinterest',
    icon: '📌',
    versions: ['v1'],
    latestVersion: 'v1',
    description: 'Export for Pinterest Bulk Editor',
  },
};

/**
 * Get a platform writer by platform and optional version
 * Defaults to latest version if not specified
 *
 * @param platform - The advertising platform
 * @param version - Optional version (defaults to 'latest')
 * @returns PlatformWriter instance
 * @throws Error if platform or version not found
 */
export function getWriter(platform: Platform, version?: string): PlatformWriter {
  const platformWriters = WRITERS[platform];

  if (!platformWriters) {
    throw new Error(`Unknown platform: ${platform}`);
  }

  const writerFactory = version
    ? platformWriters[version]
    : platformWriters['latest'];

  if (!writerFactory) {
    throw new Error(
      `Unknown version '${version}' for platform '${platform}'. ` +
        `Available versions: ${Object.keys(platformWriters).filter((v) => v !== 'latest').join(', ')}`
    );
  }

  return writerFactory();
}

/**
 * Get information about all available platforms
 *
 * @returns Array of PlatformInfo objects
 */
export function getAvailablePlatforms(): PlatformInfo[] {
  return Object.values(PLATFORM_INFO);
}

/**
 * Get information about a specific platform
 *
 * @param platform - The platform ID
 * @returns PlatformInfo or undefined if not found
 */
export function getPlatformInfo(platform: Platform): PlatformInfo | undefined {
  return PLATFORM_INFO[platform];
}

/**
 * Check if a platform is supported
 *
 * @param platform - The platform ID to check
 * @returns true if platform is supported
 */
export function isPlatformSupported(platform: string): platform is Platform {
  return platform in WRITERS;
}

/**
 * Get all supported platform IDs
 *
 * @returns Array of Platform IDs
 */
export function getSupportedPlatforms(): Platform[] {
  return Object.keys(WRITERS) as Platform[];
}
